import { useMemo } from 'react';
import { Text } from '@fluentui/react-components';
import { Box } from '../../../../theme/fluent';
import type { FluentUIDesignKitTokens } from '../../../../theme/fluent';

interface MarkdownStyleProps {
  tokens: FluentUIDesignKitTokens;
  modeTokens: FluentUIDesignKitTokens['light'];
  defaultTextColor: string;
}

interface MarkdownComponentProps {
  stopPropagation?: boolean;
}

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type TextSize = 200 | 300 | 400 | 500 | 600;

export const useMarkdownComponents = ({
  tokens,
  modeTokens,
  defaultTextColor,
  stopPropagation,
}: MarkdownStyleProps & MarkdownComponentProps) => {
  return useMemo(() => {
    const getHeadingConfig = (tag: HeadingTag): { size: TextSize; marginBottom: string } => {
      switch (tag) {
        case 'h1':
          return { size: 600, marginBottom: tokens.spacingVerticalM };
        case 'h2':
          return { size: 500, marginBottom: tokens.spacingVerticalS };
        case 'h3':
          return { size: 400, marginBottom: tokens.spacingVerticalXS };
        case 'h4':
          return { size: 300, marginBottom: tokens.spacingVerticalXXS };
        default:
          return { size: 200, marginBottom: tokens.spacingVerticalXXS };
      }
    };

    const heading = (tag: HeadingTag) => {
      const { size, marginBottom } = getHeadingConfig(tag);
      return ({ children }: { children?: React.ReactNode }) => (
        <Text
          as={tag}
          size={size}
          weight="semibold"
          style={{ display: 'block', marginBottom, color: defaultTextColor }}
        >
          {children}
        </Text>
      );
    };

    return {
      p: ({ children }: React.ComponentPropsWithoutRef<'p'>) => (
        <Text
          as="p"
          style={{
            display: 'block',
            marginBottom: tokens.spacingVerticalXS,
            lineHeight: '1.5',
            color: defaultTextColor,
          }}
        >
          {children}
        </Text>
      ),
      h1: heading('h1'),
      h2: heading('h2'),
      h3: heading('h3'),
      h4: heading('h4'),
      h5: heading('h5'),
      h6: heading('h6'),
      ul: ({ children }: React.ComponentPropsWithoutRef<'ul'>) => (
        <ul style={{ marginBottom: tokens.spacingVerticalXS, paddingLeft: tokens.spacingHorizontalL }}>{children}</ul>
      ),
      ol: ({ children }: React.ComponentPropsWithoutRef<'ol'>) => (
        <ol style={{ marginBottom: tokens.spacingVerticalXS, paddingLeft: tokens.spacingHorizontalL }}>{children}</ol>
      ),
      li: ({ children }: React.ComponentPropsWithoutRef<'li'>) => (
        <li style={{ marginBottom: tokens.spacingVerticalXXS }}>
          <Text style={{ color: defaultTextColor }}>{children}</Text>
        </li>
      ),
      strong: ({ children }: React.ComponentPropsWithoutRef<'strong'>) => (
        <strong style={{ color: defaultTextColor }}>{children}</strong>
      ),
      em: ({ children }: React.ComponentPropsWithoutRef<'em'>) => (
        <em style={{ color: defaultTextColor }}>{children}</em>
      ),
      code: ({ children }: React.ComponentPropsWithoutRef<'code'>) => (
        <Text
          font="monospace"
          style={{
            backgroundColor: modeTokens.colorNeutralBackground3,
            color: modeTokens.colorNeutralForeground1,
            padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXXS}`,
            borderRadius: tokens.borderRadiusSmall,
            fontSize: tokens.fontSizeBase200,
          }}
        >
          {children}
        </Text>
      ),
      a: ({ href, children }: React.ComponentPropsWithoutRef<'a'>) => (
        <a
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
          }}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: modeTokens.colorBrand80, textDecoration: 'underline' }}
        >
          {children}
        </a>
      ),
      br: () => <br />,
      pre: ({ children }: React.ComponentPropsWithoutRef<'pre'>) => (
        <Box
          style={{
            backgroundColor: modeTokens.colorNeutralBackground3,
            padding: tokens.spacingVerticalM,
            borderRadius: tokens.borderRadiusSmall,
            marginBottom: tokens.spacingVerticalXS,
            overflow: 'auto',
          }}
        >
          <Text font="monospace" size={300} style={{ color: modeTokens.colorNeutralForeground1 }}>
            {children}
          </Text>
        </Box>
      ),
      table: ({ children }: React.ComponentPropsWithoutRef<'table'>) => (
        <Box
          as="table"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: tokens.spacingVerticalM,
          }}
        >
          {children}
        </Box>
      ),
      thead: ({ children }: React.ComponentPropsWithoutRef<'thead'>) => <thead>{children}</thead>,
      tbody: ({ children }: React.ComponentPropsWithoutRef<'tbody'>) => <tbody>{children}</tbody>,
      tr: ({ children }: React.ComponentPropsWithoutRef<'tr'>) => (
        <tr style={{ borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}` }}>{children}</tr>
      ),
      th: ({ children }: React.ComponentPropsWithoutRef<'th'>) => (
        <th
          style={{
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            textAlign: 'left',
            fontWeight: tokens.fontWeightSemibold,
            color: modeTokens.colorNeutralForeground1,
            fontSize: tokens.fontSizeBase300,
            backgroundColor: modeTokens.colorNeutralBackground3,
            borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}`,
          }}
        >
          {children}
        </th>
      ),
      td: ({ children }: React.ComponentPropsWithoutRef<'td'>) => (
        <td
          style={{
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            color: defaultTextColor,
            fontSize: tokens.fontSizeBase300,
          }}
        >
          {children}
        </td>
      ),
    };
  }, [tokens, modeTokens, defaultTextColor]);
};
